import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { SmartDataTable } from '@src/components/SmartDataTable';
import { useTreatmentsColumns } from './hooks/useTreatmentsColumns';
import { useTreatmentsFormFields } from './hooks/useTreatmentsFormFields';
import { CopyOutlined } from '@ant-design/icons';
import { message } from 'antd';

interface TreatmentsTabProps {
  patientId?: string | number;
  autoEditTreatmentId?: string | null;
  treatmentInitialValues?: Record<string, any>;
  autoOpenCreate?: boolean;
}

function normalizeCommaOrArray(val: unknown): string[] {
  if (Array.isArray(val)) {
    return val.filter(Boolean).map(String);
  }
  if (typeof val === 'string' && val.trim()) {
    return val
      .split(',')
      .map(s => s.trim())
      .filter(Boolean);
  }
  return [];
}

export const TreatmentsTab: React.FC<TreatmentsTabProps> = ({
  patientId,
  autoEditTreatmentId,
  treatmentInitialValues,
  autoOpenCreate = false,
}) => {
  const columns = useTreatmentsColumns();
  const formFields = useTreatmentsFormFields(patientId);
  const [storedInitialValues, setStoredInitialValues] = useState<
    Record<string, any> | undefined
  >(undefined);

  const [copyTreatmentData, setCopyTreatmentData] = useState<any>(null);
  const [copyAutoOpen, setCopyAutoOpen] = useState(false);
  const [copyId, setCopyId] = useState(0);
  const [formInstanceKey, setFormInstanceKey] = useState('');

  useEffect(() => {
    if (treatmentInitialValues) {
      setStoredInitialValues(treatmentInitialValues);
    }
  }, [treatmentInitialValues]);

  useEffect(() => {
    setStoredInitialValues(undefined);
    setCopyTreatmentData(null);
    setCopyAutoOpen(false);
    setCopyId(0);
    setFormInstanceKey('');
  }, [patientId]);

  useEffect(() => {
    return () => {
      setCopyTreatmentData(null);
      setCopyAutoOpen(false);
      setCopyId(0);
      setFormInstanceKey('');
    };
  }, []);

  useEffect(() => {
    if (copyTreatmentData && !copyAutoOpen && !autoOpenCreate) {
      const timer = setTimeout(() => {
        setCopyTreatmentData(null);
        setCopyId(0);
        setFormInstanceKey('');
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [copyTreatmentData, copyAutoOpen, autoOpenCreate]);

  const handleCreateSuccess = useCallback(() => {
    setCopyTreatmentData(null);
    setCopyAutoOpen(false);
    setCopyId(0);
    setFormInstanceKey('');
  }, []);

  const handleCreateDrawerClose = useCallback(() => {
    setCopyTreatmentData(null);
    setCopyAutoOpen(false);
    setCopyId(0);
    setFormInstanceKey('');
  }, []);

  const onCopyClick = useCallback(
    (record: any) => {
      if (!record) {
        console.warn('No record provided for copying');
        return;
      }

      if (copyTreatmentData) {
        message.warning(
          'Please complete or cancel the current copy operation first.'
        );
        return;
      }

      try {
        const newCopyId = copyId + 1;
        const timestamp = Date.now();
        const uniqueFormKey = `form_${timestamp}_${newCopyId}_${Math.random().toString(36).substr(2, 9)}`;

        setCopyId(newCopyId);
        setFormInstanceKey(uniqueFormKey);

        const wl = record.wavelength;
        const wavelength =
          wl != null && wl !== '' && String(wl) !== '0' ? wl : undefined;

        const copyData = {
          copyId: newCopyId,
          timestamp,
          formInstanceKey: uniqueFormKey,
          patient_id: patientId,
          under_observation_doctor_user_id:
            record.under_observation_doctor_user_id,
          therapist_id: record.therapist_id,
          therapist_type: record.therapist_type,
          treatment_name: normalizeCommaOrArray(record.treatment_name),
          body_part: normalizeCommaOrArray(record.body_part),
          session_number: record.session_number,
          skin_type: record.skin_type,
          machine_name: record.machine_name || '',
          machine_starting_reading: record.machine_starting_reading ?? '',
          machine_ending_reading: record.machine_ending_reading ?? '',
          power_duration: normalizeCommaOrArray(record.power_duration),
          wavelength,
          remark: record.remark || '',
        };

        setCopyTreatmentData(copyData);
        setCopyAutoOpen(true);

        message.success('Treatment copied successfully!');
      } catch (error) {
        console.error('Error copying treatment data:', error);
        message.error('Failed to copy treatment. Please try again.');
      }
    },
    [copyTreatmentData, copyId, patientId]
  );

  const uniqueFormFields = useMemo(() => {
    if (!copyTreatmentData || !formInstanceKey) return formFields;

    return formFields.map(field => ({
      ...field,
      _uniqueId: `${(field as { name?: string }).name}_${formInstanceKey}`,
      _copyOperationId: copyTreatmentData.copyId,
    }));
  }, [formFields, copyTreatmentData, formInstanceKey]);

  const customActions = useMemo(
    () => [
      {
        key: 'copy',
        icon: <CopyOutlined />,
        label: '',
        onClick: onCopyClick,
        type: 'default' as const,
        tooltip: 'Copy Treatment',
      },
    ],
    [onCopyClick]
  );

  const effectiveInitialValues = storedInitialValues || treatmentInitialValues;

  const createFormInitialValues = useMemo(() => {
    if (!effectiveInitialValues) {
      return { patient_id: patientId };
    }
    return {
      patient_id: patientId,
      ...effectiveInitialValues,
    };
  }, [patientId, storedInitialValues, treatmentInitialValues]);

  const createInitialValues = copyTreatmentData
    ? {
        ...copyTreatmentData,
        _copyTimestamp: copyTreatmentData.timestamp,
        _copyId: copyTreatmentData.copyId,
        _formInstanceKey: copyTreatmentData.formInstanceKey,
      }
    : createFormInitialValues;

  if (!patientId) {
    return null;
  }

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <SmartDataTable
        key={formInstanceKey || `treatments-table-${patientId}`}
        api={{
          resource: `treatments`,
          defaultParams: {
            filters: [
              {
                field: 'patient_id',
                operator: 'eq',
                value: patientId,
              },
            ],
          },
        }}
        title="Treatments"
        columns={columns}
        createForm={{
          fields: uniqueFormFields as any,
          title: 'Create Treatment',
          submitText: 'Create Treatment',
          initialValues: createInitialValues,
        }}
        editForm={{
          fields: uniqueFormFields as any,
          title: 'Edit Treatment',
          submitText: 'Save Changes',
        }}
        formDisplayMode="drawer"
        drawerWidth={600}
        showCreate={true}
        showEdit={true}
        showDelete={true}
        showView={false}
        isFilter={false}
        pageSize={10}
        pageOptions={[10, 20, 50, 100]}
        showSizeChanger={true}
        showQuickJumper={false}
        showExport={false}
        showColumnVisibility={true}
        tableProps={{
          scroll: {
            x: 'max-content',
            y: 'calc(100vh - 375px)',
          },
        }}
        rowKey="id"
        style={{
          height: '100%',
        }}
        autoOpenEditId={autoEditTreatmentId}
        autoOpenCreate={autoOpenCreate || copyAutoOpen}
        customActions={customActions}
        onCreateSuccess={handleCreateSuccess}
        onCreateDrawerClose={handleCreateDrawerClose}
      />
    </div>
  );
};
